import { useEffect } from 'react';
import { create } from 'zustand';
import { useMediaQuery } from 'react-responsive';
import Breakpoints from '../../config/breakpoints.json';

interface ResponsiveState {
  // Specific breakpoints
  isSmallPhone: boolean;
  isRegularPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  isExtraLargeDesktop: boolean;
  
  // Breakpoint and below
  isPhoneAndBelow: boolean;
  isTabletAndBelow: boolean;
  isDesktopAndBelow: boolean;
  isLargeDesktopAndBelow: boolean;
  
  // Breakpoint and above
  isPhoneAndAbove: boolean;
  isTabletAndAbove: boolean;
  isDesktopAndAbove: boolean;
  isLargeDesktopAndAbove: boolean;
  
  // Actions
  setBreakpoint: (
    key: keyof Omit<ResponsiveState, 'setBreakpoint'>,
    value: boolean
  ) => void;
}

export const useResponsiveStore = create<ResponsiveState>()((set) => ({
  // Specific breakpoints
  isSmallPhone: false,
  isRegularPhone: false,
  isTablet: false,
  isDesktop: false,
  isLargeDesktop: false,
  isExtraLargeDesktop: false,
  
  // Breakpoint and below
  isPhoneAndBelow: false,
  isTabletAndBelow: false,
  isDesktopAndBelow: false,
  isLargeDesktopAndBelow: false,
  
  // Breakpoint and above
  isPhoneAndAbove: false,
  isTabletAndAbove: false,
  isDesktopAndAbove: false,
  isLargeDesktopAndAbove: false,
  
  // Actions
  setBreakpoint: (key, value) => set({ [key]: value }),
}));

// Hook to initialize responsive state
export const useInitializeResponiveState = () => {
  // Specific breakpoints
  const isSmallPhone = useMediaQuery({ maxWidth: Breakpoints.SM - 1 });
  const isRegularPhone = useMediaQuery({
    minWidth: Breakpoints.SM,
    maxWidth: Breakpoints.MD - 1,
  });
  const isTablet = useMediaQuery({
    minWidth: Breakpoints.MD,
    maxWidth: Breakpoints.LG - 1,
  });
  const isDesktop = useMediaQuery({
    minWidth: Breakpoints.LG,
    maxWidth: Breakpoints.XL - 1,
  });
  const isLargeDesktop = useMediaQuery({
    minWidth: Breakpoints.XL,
    maxWidth: Breakpoints.XXL - 1,
  });
  const isExtraLargeDesktop = useMediaQuery({
    minWidth: Breakpoints.XXL,
  });

  // Breakpoint and below
  const isPhoneAndBelow = useMediaQuery({ maxWidth: Breakpoints.MD - 1 });
  const isTabletAndBelow = useMediaQuery({ maxWidth: Breakpoints.LG - 1 });
  const isDesktopAndBelow = useMediaQuery({ maxWidth: Breakpoints.XL - 1 });
  const isLargeDesktopAndBelow = useMediaQuery({ maxWidth: Breakpoints.XXL - 1 });

  // Breakpoint and above
  const isPhoneAndAbove = useMediaQuery({ minWidth: Breakpoints.SM });
  const isTabletAndAbove = useMediaQuery({ minWidth: Breakpoints.MD });
  const isDesktopAndAbove = useMediaQuery({ minWidth: Breakpoints.LG });
  const isLargeDesktopAndAbove = useMediaQuery({ minWidth: Breakpoints.XL });

  const { setBreakpoint } = useResponsiveStore();

  useEffect(() => {
    // Specific breakpoints
    setBreakpoint('isSmallPhone', isSmallPhone);
    setBreakpoint('isRegularPhone', isRegularPhone);
    setBreakpoint('isTablet', isTablet);
    setBreakpoint('isDesktop', isDesktop);
    setBreakpoint('isLargeDesktop', isLargeDesktop);
    setBreakpoint('isExtraLargeDesktop', isExtraLargeDesktop);
    
    // Breakpoint and below
    setBreakpoint('isPhoneAndBelow', isPhoneAndBelow);
    setBreakpoint('isTabletAndBelow', isTabletAndBelow);
    setBreakpoint('isDesktopAndBelow', isDesktopAndBelow);
    setBreakpoint('isLargeDesktopAndBelow', isLargeDesktopAndBelow);
    
    // Breakpoint and above
    setBreakpoint('isPhoneAndAbove', isPhoneAndAbove);
    setBreakpoint('isTabletAndAbove', isTabletAndAbove);
    setBreakpoint('isDesktopAndAbove', isDesktopAndAbove);
    setBreakpoint('isLargeDesktopAndAbove', isLargeDesktopAndAbove);
  }, [
    isSmallPhone, isRegularPhone, isTablet, isDesktop, isLargeDesktop, isExtraLargeDesktop,
    isPhoneAndBelow, isTabletAndBelow, isDesktopAndBelow, isLargeDesktopAndBelow,
    isPhoneAndAbove, isTabletAndAbove, isDesktopAndAbove, isLargeDesktopAndAbove,
    setBreakpoint
  ]);
};

// Export selectors for convenience
export const useSmallPhoneMediaQuery = () => useResponsiveStore((state) => state.isSmallPhone);
export const useRegularPhoneMediaQuery = () => useResponsiveStore((state) => state.isRegularPhone);
export const useTabletMediaQuery = () => useResponsiveStore((state) => state.isTablet);
export const useDesktopMediaQuery = () => useResponsiveStore((state) => state.isDesktop);
export const useLargeDesktopMediaQuery = () => useResponsiveStore((state) => state.isLargeDesktop);
export const useExtraLargeDesktopMediaQuery = () => useResponsiveStore((state) => state.isExtraLargeDesktop);

// Breakpoint and below selectors
export const usePhoneAndBelowMediaQuery = () => useResponsiveStore((state) => state.isPhoneAndBelow);
export const useTabletAndBelowMediaQuery = () => useResponsiveStore((state) => state.isTabletAndBelow);
export const useDesktopAndBelowMediaQuery = () => useResponsiveStore((state) => state.isDesktopAndBelow);
export const useLargeDesktopAndBelowMediaQuery = () => useResponsiveStore((state) => state.isLargeDesktopAndBelow);

// Breakpoint and above selectors
export const usePhoneAndAboveMediaQuery = () => useResponsiveStore((state) => state.isPhoneAndAbove);
export const useTabletAndAboveMediaQuery = () => useResponsiveStore((state) => state.isTabletAndAbove);
export const useDesktopAndAboveMediaQuery = () => useResponsiveStore((state) => state.isDesktopAndAbove);
export const useLargeDesktopAndAboveMediaQuery = () => useResponsiveStore((state) => state.isLargeDesktopAndAbove); 